import React, { useState, useEffect } from 'react';
import { Container, Card, Button, Spinner, Alert, Row, Col } from 'react-bootstrap';
import axios from 'axios';
import { getApiUrl, API_ENDPOINTS, getAuthHeaders } from '../../config/api.config';
import './Payments.css';
import { Flex, Heading, Text } from '@radix-ui/themes';

const Payments = () => {
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isCheckoutLoading, setIsCheckoutLoading] = useState(false);
  const [hasActiveSubscription, setHasActiveSubscription] = useState(false);
  const [hasStripeAccount, setHasStripeAccount] = useState(false);
  const [portalUrl, setPortalUrl] = useState(null);
  const [selectedBillingOption, setSelectedBillingOption] = useState({
    personal: 'monthly',
    classroom: 'annual'
  });
  const [userRole, setUserRole] = useState('');

  useEffect(() => {
    // Get the user role from localStorage when component mounts
    // Get the user object from localStorage
    const userString = localStorage.getItem('user');
    let userRole = '';
    
    if (userString) {
      try {
        const user = JSON.parse(userString);
        userRole = user.role?.toLowerCase();
      } catch (e) {
        console.error('Error parsing user data:', e);
      }
    }
    
    // Fallback to direct role storage if user object doesn't have role
    if (!userRole) {
      userRole = localStorage.getItem('role')?.toLowerCase();
    }
    
    setUserRole(userRole || '');
    
    const fetchSubscription = async () => {
      try {
        const response = await axios.get(
          getApiUrl(API_ENDPOINTS.PAYMENT.SUBSCRIPTION),
          { headers: getAuthHeaders() }
        );

        if (response.data.success) {
          // Set subscription data
          setSubscription(response.data.subscription);

          // Check if the user has any active subscription (paid or trial)
          const subData = response.data.subscription;
          if (subData && subData.status === 'active') {
            setHasActiveSubscription(true);
          }
          
          // Check if this is a trial account
          if (response.data.isTrial) {
            // For trial accounts, set a flag in the subscription object
            setSubscription(prev => ({ 
              ...prev, 
              isTrial: true 
            }));
            // Trial users should see subscription options
            setHasActiveSubscription(false);
          }

          // Check if they have a Stripe account with portal URL
          if (response.data.hasStripeAccount) {
            setHasStripeAccount(true);
          }
          
          // Save portal URL if available
          if (response.data.portalUrl) {
            setPortalUrl(response.data.portalUrl);
          }

        } else {
          setError(response.data.message || 'Failed to load subscription information');
        }
      } catch (error) {
        console.error('Error fetching subscription:', error);
        setError('Could not load subscription details. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchSubscription();
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleManageSubscription = async () => {
    try {
      setLoading(true);
      
      // If we already have a portal URL, use it directly
      if (portalUrl) {
        window.location.href = portalUrl;
        return;
      }
      
      // Otherwise call the API to create a new portal session
      const response = await axios.post(
        getApiUrl('/api/v1/stem@home/payment/create-portal-session'),
        {
          returnUrl: window.location.origin + '/dashboard'
        },
        { headers: getAuthHeaders() }
      );
      
      // Redirect to Stripe portal URL
      if (response.data.success && response.data.url) {
        window.location.href = response.data.url;
      } else {
        setError(response.data.message || 'Failed to access payment portal');
      }
    } catch (error) {
      console.error('Error accessing payment portal:', error);
      setError('Could not access payment portal. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCheckoutSession = async (planType) => {
    try {
      setIsCheckoutLoading(true);
      
      // Get billing option for the selected plan
      const billingOption = selectedBillingOption[planType];
      
      if (!planType || !billingOption) {
        setError('Missing required plan information. Please try again.');
        setIsCheckoutLoading(false);
        return;
      }
      
      // Call the API to create a checkout session with both parameters
      const response = await axios.post(
        getApiUrl('/api/v1/stem@home/payment/create-authenticated-checkout'),
        { 
          planType,
          billingOption
        },
        { headers: getAuthHeaders() }
      );
      
      // Redirect to Stripe checkout URL
      if (response.data.success && response.data.checkoutUrl) {
        window.location.href = response.data.checkoutUrl;
      } else {
        setError(response.data.message || 'Failed to create checkout session');
      }
    } catch (error) {
      console.error('Error creating checkout session:', error);
      const errorMessage = error.response?.data?.message || 'Could not create checkout session. Please try again later.';
      setError(errorMessage);
    } finally {
      setIsCheckoutLoading(false);
    }
  };

  const handleBillingOptionChange = (planType, option) => {
    setSelectedBillingOption(prev => ({
      ...prev,
      [planType]: option
    }));
  };

  if (loading) {
    return (
      <Container className="py-5">
        <div className="text-center">
          <Spinner animation="border" role="status" className="mb-3" />
          <h3>Loading subscription information...</h3>
        </div>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="py-5">
        <Alert variant="danger">
          <Alert.Heading>Error</Alert.Heading>
          <p>{error}</p>
          <div className="d-flex justify-content-end">
            <Button 
              variant="outline-danger" 
              onClick={() => {
                setError(null);
                setLoading(false);
              }}
            >
              Try Again
            </Button>
          </div>
        </Alert>
      </Container>
    );
  }
  
  // If they have a Stripe account with portal access, just show simplified portal access
  if (hasStripeAccount && portalUrl && !subscription?.isTrial) {
    return (
      <Container className="py-5">
        <Card className="subscription-card mb-4 shadow-sm text-center">
          <Card.Header className="bg-light">
            <h3 className="mb-0">Subscription Management</h3>
          </Card.Header>
          <Card.Body className="py-5">
            <p className="lead mb-4">
              Your subscription is managed through Stripe.
            </p>
            
            <Button 
              variant="primary" 
              size="lg" 
              onClick={handleManageSubscription}
              disabled={loading}
              className="manage-subscription-btn"
            >
              {loading ? 'Loading...' : 'Manage on Stripe'}
            </Button>
            
            <div className="mt-4 text-muted">
              <p>You will be redirected to Stripe's secure customer portal to manage your payment details, 
              view billing history, or update your subscription.</p>
            </div>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  // Only show payment options if user doesn't have an active subscription
  if (!hasActiveSubscription) {
    return (
      <Container className="pricing-container py-5">
        <div className="text-center mb-4">
          <h1>Choose the plan that best suits your needs</h1>
          <p className="text-muted">All prices include tax</p>
        </div>
        
        <Row>
          <Col lg={4} className="mb-4">
            <Card className="pricing-card h-100">
              <Card.Body className="d-flex flex-column">
                <h2 className="plan-title">Personal Learning Plan</h2>
                <p className="mb-3">Perfect for individual tutors, homeschoolers, and private teaching</p>
                
                <div className="billing-toggle mb-3">
                  <div className="d-flex justify-content-center">
                    <div className={`toggle-option ${selectedBillingOption.personal === 'monthly' ? 'active' : ''}`}>
                      <Button 
                        variant={selectedBillingOption.personal === 'monthly' ? 'primary' : 'light'}
                        onClick={() => handleBillingOptionChange('personal', 'monthly')}
                        className="rounded-pill"
                      >
                        Monthly
                      </Button>
                    </div>
                    <div className={`toggle-option ${selectedBillingOption.personal === 'annual' ? 'active' : ''}`}>
                      <Button 
                        variant={selectedBillingOption.personal === 'annual' ? 'primary' : 'light'}
                        onClick={() => handleBillingOptionChange('personal', 'annual')}
                        className="rounded-pill ms-2"
                      >
                        Annual
                      </Button>
                    </div>
                  </div>
                </div>
                
                <div className="price-container">
                  {selectedBillingOption.personal === 'annual' && (
                    <div className="saving text-success">Save 18% with annual billing!</div>
                  )}
                  <div className="current-price">
                    <span className="currency">£</span>
                    <span className="amount">
                      {selectedBillingOption.personal === 'monthly' ? '4.99' : '50.91'}
                    </span>
                    <span className="period">
                      /{selectedBillingOption.personal === 'monthly' ? 'month' : 'year'}
                    </span>
                  </div>
                </div>
                
                <ul className="list-unstyled my-4 flex-grow-1">
                  <li>✓ 1 Teacher account</li>
                  <li>✓ 1 Student maximum</li>
                  <li>✓ 5 Classes maximum</li>
                  <li>✓ 50 Total questions maximum</li>
                  <li>✓ 10 Questions per class maximum</li>
                  <li>✓ 3 Attempts per question</li>
                  <li>✓ Basic support</li>
                </ul>
                
                <Button 
                  variant="primary" 
                  size="lg" 
                  className="mt-auto"
                  onClick={() => handleCreateCheckoutSession('personal')}
                  disabled={isCheckoutLoading}
                >
                  {isCheckoutLoading ? 'Loading...' : 'Get Started'}
                </Button>
              </Card.Body>
            </Card>
          </Col>

          <Col lg={4} className="mb-4">
            <Card className="pricing-card h-100 border-primary">
              <div className="bg-primary text-white text-center py-2">
                <small>MOST POPULAR</small>
              </div>
              <Card.Body className="d-flex flex-column">
                <h2 className="plan-title">Classroom Success Plan</h2>
                <p className="mb-3">Perfect for teachers, small schools, and tutoring centers</p>
                
                <div className="billing-toggle mb-3">
                  <div className="d-flex justify-content-center">
                    <div className={`toggle-option ${selectedBillingOption.classroom === 'monthly' ? 'active' : ''}`}>
                      <Button 
                        variant={selectedBillingOption.classroom === 'monthly' ? 'primary' : 'light'}
                        onClick={() => handleBillingOptionChange('classroom', 'monthly')}
                        className="rounded-pill"
                      >
                        Monthly
                      </Button>
                    </div>
                    <div className={`toggle-option ${selectedBillingOption.classroom === 'annual' ? 'active' : ''}`}>
                      <Button 
                        variant={selectedBillingOption.classroom === 'annual' ? 'primary' : 'light'}
                        onClick={() => handleBillingOptionChange('classroom', 'annual')}
                        className="rounded-pill ms-2"
                      >
                        Annual
                      </Button>
                    </div>
                  </div>
                </div>
                
                <div className="price-container">
                  {selectedBillingOption.classroom === 'annual' && (
                    <div className="saving text-success">Save 15% with annual billing!</div>
                  )}
                  <div className="current-price">
                    <span className="currency">£</span>
                    <span className="amount">
                      {selectedBillingOption.classroom === 'monthly' ? '29.99' : '305.91'}
                    </span>
                    <span className="period">
                      /{selectedBillingOption.classroom === 'monthly' ? 'month' : 'year'}
                    </span>
                  </div>
                </div>
                
                <ul className="list-unstyled my-4 flex-grow-1">
                  <li>✓ 1 Teacher account</li>
                  <li>✓ 30 Students maximum</li>
                  <li>✓ Unlimited classes</li>
                  <li>✓ Unlimited questions</li>
                  <li>✓ Unlimited questions per class</li>
                  <li>✓ 3 Attempts per question</li>
                  <li>✓ Priority support</li>
                  <li>✓ Advanced analytics</li>
                </ul>
                
                <Button 
                  variant="primary" 
                  size="lg" 
                  className="mt-auto"
                  onClick={() => handleCreateCheckoutSession('classroom')}
                  disabled={isCheckoutLoading}
                >
                  {isCheckoutLoading ? 'Loading...' : 'Get Started'}
                </Button>
              </Card.Body>
            </Card>
          </Col>

          <Col lg={4} className="mb-4">
            <Card className="pricing-card h-100">
              <Card.Body className="d-flex flex-column">
                <h2 className="plan-title">Institution Growth Plan</h2>
                <p className="mb-3">Perfect for schools, colleges, and large educational institutions</p>
                
                <div className="price-container">
                  <div className="current-price">
                    <span className="currency">Contact</span>
                    <span className="amount">Sales</span>
                  </div>
                  <p className="text-muted mt-2">Custom pricing based on your needs</p>
                </div>
                
                <ul className="list-unstyled my-4 flex-grow-1">
                  <li>✓ 1 Admin account</li>
                  <li>✓ 20 Teacher accounts</li>
                  <li>✓ 100 Students maximum</li>
                  <li>✓ Unlimited classes</li>
                  <li>✓ Unlimited questions</li>
                  <li>✓ Unlimited questions per class</li>
                  <li>✓ 3 Attempts per question</li>
                  <li>✓ Premium support</li>
                  <li>✓ Advanced analytics</li>
                  <li>✓ Custom integrations</li>
                  <li>✓ Training & onboarding</li>
                </ul>
                
                <Button 
                  variant="outline-primary" 
                  size="lg" 
                  className="mt-auto"
                  onClick={() => window.open('mailto:francis.jones@snapgrade.ai?subject=Institution Growth Plan Inquiry', '_blank')}
                >
                  Contact Sales
                </Button>
              </Card.Body>
            </Card>
          </Col>
        </Row>
        
        <div className="text-center mt-4 text-muted">
          <p>You will be redirected to Stripe's secure checkout page to complete your subscription.</p>
          <p>For more information about our plans, please contact <a href="mailto:support@snapgrade.com">support@snapgrade.com</a></p>
        </div>
      </Container>
    );
  }

  // Simplified subscription management for existing subscribers
  return (
    <Container className="py-5">
      <h1 className="mb-4">Subscription Management</h1>
      
      <Card className="subscription-card mb-4 shadow-sm">
        <Card.Header className="d-flex justify-content-between align-items-center bg-light">
          <h3 className="mb-0">Current Subscription</h3>
          <span className={`badge ${subscription.status === 'active' ? 'bg-success' : 'bg-warning'}`}>
            {subscription.status || 'Unknown'}
          </span>
        </Card.Header>
        <Card.Body className="text-center">
          <p className="lead mb-4">
            You have an active {subscription.isTrial ? 'trial' : ''} subscription with SnapGrade.
            {subscription.planName && (
              <span className="d-block mt-2 text-muted">Plan: {subscription.planName}</span>
            )}
          </p>
          
          <div className="d-flex justify-content-center">
            <Button 
              variant="primary" 
              size="lg" 
              onClick={handleManageSubscription}
              disabled={loading}
              className="manage-subscription-btn"
            >
              {loading ? 'Loading...' : 'Manage on Stripe'}
            </Button>
          </div>
          
          <div className="mt-4 text-muted">
            <p>You will be redirected to Stripe's secure customer portal to manage your payment details, 
            view billing history, or update your subscription.</p>
          </div>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default Payments; 